import api from '../../services/api';
import { trackOrderWrite } from '../../hooks/usePendingOrders';
import type { Order, OrderStatus, PaymentMethod, PaymentStatus } from '../../types/Order';

export interface PaymentChange {
    status: PaymentStatus;
    method?: PaymentMethod;
}

/** Every write answers the order as the API now sees it (contract 1.1, `PanelOrderDetail`). */
export const putStatus = async (id: string, status: OrderStatus) =>
    (await api.put<Order>(`/orders/${id}/update/status`, { status })).data;

export const putPayment = async (id: string, p: PaymentChange) => {
    if (p.method) await api.put(`/orders/${id}/update/payment-method`, { paymentMethod: p.method });
    return (await api.put<Order>(`/orders/${id}/update/payment-status`, { paymentStatus: p.status })).data;
};

/**
 * The one place an order write is sent from, for the board, the table view, the history and the detail page.
 * It keeps the sidebar badge right: moves it at once, rolls it back on failure, re-fetches it after the answer.
 */
export const saveOrder = (before: Order, change: Partial<Order>, send: () => Promise<Order>): Promise<Order> => {
    const pending = (s: OrderStatus) => (s === 'pending' ? 1 : 0);
    const delta = change.status ? pending(change.status) - pending(before.status) : 0;
    return trackOrderWrite(delta, send);
};

/** A label that is only digits reads "Table 12"; any other label ("Terrace 4", "Bar") is shown as written. */
export const isTableNumber = (label: string) => /^\d+$/.test(label);

/** Orders leave the board when finished. */
export const isActive = (status: OrderStatus) => status !== 'completed' && status !== 'cancelled';

export const itemCount = (order: { orderItems: { quantity: number }[] }) =>
    order.orderItems.reduce((sum, i) => sum + i.quantity, 0);
